import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useCreateDialog } from "../hooks/useCreateDialog";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { getWithRetry, postWithRetry, isUnknownAnswer } from "../lib/fetchWithRetry";
import { markSurfaceNotificationsRead } from "../lib/notificationsRead";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  Plus, Target as TargetIcon, Sparkles, Loader2,
  Wrench, Radar, Dumbbell, Brain, Check, RotateCw,
} from "lucide-react";
import { toast } from "sonner";
import PlayerIdentity from "./PlayerIdentity";
import Reflections from "./Reflections";
import { AI_COACH_ENABLED } from "../lib/featureFlags";
import { computePlanGate } from "../lib/planGate";
import { GOAL_CATEGORY_LABELS } from "../lib/goalCategory";
import PlanScreen from "../components/PlanScreen";
import PlanMilestonesScreen from "../components/PlanMilestonesScreen";
import PlanGoalsList from "../components/PlanGoalsList";
import NewGoalSheet from "../components/NewGoalSheet";
import DevelopmentChrome from "../components/DevelopmentChrome";
import PlanComingUp from "../components/PlanComingUp";
import { useAddedMatch } from "../hooks/useAddedMatchNotice";
import PlanMatchScreen from "../components/PlanMatchScreen";
import PlanCoachGame from "../components/PlanCoachGame";
import PlanMoreFolds from "../components/PlanMoreFolds";
import PlayerCoachFeedback from "../components/PlayerCoachFeedback";
import { SecondaryAction, SectionStrip, SectionPanel } from "../components/kit";
import { resolvePlanSection } from "../lib/planSections";

// Issue #764 review round 1, F4 — the LABEL half of this map now comes from
// `lib/goalCategory.js`, shared with `PlanMilestonesScreen.jsx`'s own read
// of the same enum. The icon and colour stay here: this list page is the
// only caller that draws either.
const CATEGORIES = {
  technical: { label: GOAL_CATEGORY_LABELS.technical, Icon: Wrench, color: "#0B2E59" },
  tactical:  { label: GOAL_CATEGORY_LABELS.tactical, Icon: Radar, color: "#D4A017" },
  physical:  { label: GOAL_CATEGORY_LABELS.physical, Icon: Dumbbell, color: "#10B981" },
  mental:    { label: GOAL_CATEGORY_LABELS.mental, Icon: Brain, color: "#8B5CF6" },
  general:   { label: GOAL_CATEGORY_LABELS.general, Icon: TargetIcon, color: "#64748B" },
  batting:   { label: GOAL_CATEGORY_LABELS.batting, Icon: TargetIcon, color: "#0B2E59" },
  bowling:   { label: GOAL_CATEGORY_LABELS.bowling, Icon: TargetIcon, color: "#D4A017" },
  fielding:  { label: GOAL_CATEGORY_LABELS.fielding, Icon: TargetIcon, color: "#84C5FF" },
};


// Issue #197: STATUSES/normalizeStatus used to be defined locally here.
// Now sourced from lib/goalStatus.js — the single shared vocabulary also
// consumed by CoachPortal.jsx (the coach-write side of the same data).

// Issue #1166 (D215.1, D215.2): a player's page alone. App.js sends a coach
// or admin to /coach-portal before this mounts; the same fact is checked here
// too, so that a future route change cannot bring the shipped coach/admin
// Development page back. It renders nothing and asks the API for nothing for
// anyone else. (The inner component holds every hook, so this check is the
// only early return.)
export default function DevelopmentPlan() {
  const { user } = useAuth();
  if (user?.role !== "player") return null;
  return <PlayerDevelopmentPlan />;
}

function PlayerDevelopmentPlan() {
  // #1150 (D349): arrived from a saved "How did it go?" - the board's pill, once.
  const addedMatch = useAddedMatch();
  const [sp, setSp] = useSearchParams();
  const [goals, setGoals] = useState([]);
  const [notes, setNotes] = useState([]);
  // `null` = not yet known (distinct from `false`) so the feedback tab
  // doesn't flash "no coach yet" before /players/me has answered — see
  // the Coach feedback fold.
  const [hasCoach, setHasCoach] = useState(null);
  // Issue #271: "we don't know" is a third state, distinct from "you have
  // none". Both reads below used to resolve into the same `[]` / `false`
  // that a genuinely-empty account produces.
  const [goalsFailed, setGoalsFailed] = useState(false);
  // Issue #763's own gate (`lib/planGate.js`) needs a fact `goals` itself
  // cannot carry: `goals` starts `[]` before the first `GET /goals`
  // response lands, indistinguishable both from a genuinely empty account
  // AND from a caller this route was never meant for.
  //
  // `goalsAnswered` is set ONLY inside the SUCCESS branch below, never in
  // `.catch` — found live, on the first flag-on responsive-harness run.
  // `goalsFailed: false` plus `goals: []` together read exactly like "a
  // real 200 confirmed zero goals" to a gate that only checked those two. An
  // earlier version of this file's gate did exactly that, when a coach could
  // still reach this page and `GET /goals` with no `player_id` answered them
  // a deterministic 400 (#1166 closed that road). `goalsAnswered` names the
  // fact the gate actually needs — "a genuine 200 landed" — so any failure
  // can never be mistaken for a confirmed empty plan.
  const [goalsAnswered, setGoalsAnswered] = useState(false);
  const [feedbackFailed, setFeedbackFailed] = useState(false);
  // Issue #763 — the gate's OWN one-shot "?new=1" read, via its OWN
  // `useCreateDialog()` instance, DELIBERATELY NOT shared with
  // the New goal sheet's. An earlier
  // version lifted this into one shared instance passed down as a prop —
  // wrong: the New goal sheet's own `open` also flips true on every
  // `dev-new-goal` CLICK (not just the URL), and since this gate's
  // `createOpen` bypasses `onGoalTab` entirely (see `lib/planGate.js`'s
  // own header), a click on ANY tab where a plan already exists swapped
  // the WHOLE PAGE to `PlanScreen` mid-interaction — the New goal sheet's own
  // `[role="dialog"]` never got a chance to open. Found live: broke every
  // `/development` cell in the responsive harness. Two independent
  // `useCreateDialog()` calls both read the SAME underlying
  // `useSearchParams()` state (react-router shares it across call sites in
  // one tree) and each strips `?new=1` itself — idempotent if the other
  // already did — so the deep-link contract (`journey 14`'s "the create
  // param is spent, not sticky") still holds for both.
  const [planCreateIntent, setPlanCreateIntent] = useCreateDialog();
  const load = () =>
    getWithRetry("/goals")
      .then((r) => { setGoals(r.data || []); setGoalsFailed(false); setGoalsAnswered(true); })
      .catch((e) => {
        // Was an uncaught `.then()` chain: the rejection went nowhere, the
        // goal list stayed `[]`, and the goals list told the player "No
        // short-term goals yet. Some ideas: …" about goals it had never
        // been sent.
        //
        // Gated on `isUnknownAnswer` because a refused request (a 4xx) is
        // an ANSWER, not an unknown. Offering a Try again button for it
        // would promise a recovery that can never happen.
        setGoalsFailed(isUnknownAnswer(e));
        console.error("DevelopmentPlan: failed to load /goals", e);
      });
  // Issue #81: GET /notes is already scoped to the caller's own player
  // profile with no role gate for reads (api/src/routes/notes.ts) — a
  // player has always been able to read their own feedback via the API,
  // there was just no UI surface for it. `has_coach` comes from the same
  // /players/me every player-facing page already calls (see players.ts) —
  // it's what lets the empty state below tell "no coach yet" apart from
  // "coach hasn't left feedback yet" without a new endpoint.
  //
  // Issue #271: `.catch(() => setHasCoach(false))` converted a failed
  // request into the assertion "you don't have a coach" — the single most
  // discouraging thing this page can say, told to a player who does. Both
  // legs now report failure as failure; the panel renders that instead of
  // choosing between two empty states it has no evidence for.
  const loadFeedback = () =>
    Promise.all([getWithRetry("/notes"), getWithRetry("/players/me")])
      .then(([n, p]) => {
        setNotes(n.data || []);
        setHasCoach(!!p.data.has_coach);
        setFeedbackFailed(false);
      })
      .catch((e) => {
        // Same coach-reachability reasoning as `load` above. On a
        // deterministic 4xx, leave `hasCoach` false so the panel keeps its
        // existing (pre-#271) rendering rather than inventing a transport
        // problem that did not happen.
        const unknown = isUnknownAnswer(e);
        setHasCoach(unknown ? null : false);
        setFeedbackFailed(unknown);
        console.error("DevelopmentPlan: failed to load coach feedback", e);
      });
  useEffect(() => { load(); loadFeedback(); }, []);

  // AI Development Plan tab is flagged off by default (spec §7/D6, §8.1;
  // issue #56) — per-token LLM cost, budget decision pending. A stale
  // `?tab=ai` in the URL (bookmark, back-button) falls back to the default
  // tab rather than rendering a tab that no longer has a trigger.
  //
  // Issue #464 splits the read in two: `chosenTab` is the tab the player
  // ASKED for (null when they just landed on `/development`), `tab` is the
  // one this page RENDERS. Only the first may clear notifications — see the
  // effect below.
  const chosenTab = sp.get("tab");
  const requestedTab = chosenTab || "short";
  const tab = requestedTab === "ai" && !AI_COACH_ENABLED ? "short" : requestedTab;
  // #1164 (flag on only): the same `?tab=` read as a section, a fold in More and
  // a goal group. `tab` and `chosenTab` stay as they are for the flag-off page
  // and for the effects below.
  const view = resolvePlanSection(chosenTab, { aiEnabled: AI_COACH_ENABLED });
  // A plain name for the section in play, so the harness can trace it to `?tab=` (e2e/tabs.js).
  const section = view.section;
  const groupRefs = { short: useRef(null), long: useRef(null) };
  // `?tab=short|long` is Goals with that group scrolled to, once the goals are in.
  useEffect(() => {
    const el = view.group && goalsAnswered ? groupRefs[view.group].current : null;
    if (el && typeof el.scrollIntoView === "function") el.scrollIntoView({ block: "start" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view.group, view.section, goalsAnswered]);

  // Issue #184 / SPEC-pilot-polish.md §3.4 item 1: "landing on the tab" is
  // the whole gesture — there's no per-note read affordance anywhere in
  // this read-only panel (the Coach feedback fold), so that's the only
  // granularity the UI actually offers. Fires on every transition INTO
  // "feedback" (both a click on the tab trigger via onValueChange above,
  // and a direct deep link from the Dashboard tile landing here with
  // ?tab=feedback already set), not on every render — the `[tab]` dep
  // means this effect is inert unless the tab identity itself changes.
  useEffect(() => {
    if (tab === "feedback") {
      // Issue #271, one of the two PROVEN sites: a failing run's server log
      // showed `POST /api/players/me/coach-feedback-seen 503`, the failure
      // was swallowed, and an unread marker survived over feedback the
      // player was looking at. So the write RETRIES, bounded — and it is
      // idempotent by construction (it stamps a watermark to "now", so
      // re-sending cannot double-apply), which is what makes the state
      // self-correcting rather than merely hopeful.
      //
      // Issue #189 removed what used to sit in front of this call: a
      // `cc:coach-feedback-seen` window event that told Layout.jsx's bell
      // to clear its badge optimistically. The bell no longer reads this
      // watermark at all — it has its own (`users.notifications_seen_at`,
      // cleared by opening the panel) and it now counts coach-assigned
      // goals and coach-scheduled sessions too, so clearing it here would
      // dismiss notifications the player has not seen. The event had no
      // other listener, so it went with the reader that needed it; the
      // optimistic-first pattern itself lives on in
      // NotificationsBell.jsx, where it is still the right shape.
      //
      // What this watermark still drives: the Dashboard's Coach feedback
      // tile, which reads it on its own next mount. Worst case (every
      // retry fails) the tile keeps saying unread until the next visit to
      // this tab stamps it again — a stale "unread" over feedback that has
      // been read, which is the harmless direction of that error. No
      // toast: there is no action for the user here.
      postWithRetry("/players/me/coach-feedback-seen")
        .catch((e) => console.error("DevelopmentPlan: failed to stamp coach-feedback-seen", e));
    }

    /**
     * Issue #417, the other half of "looked at it … by going to it on the
     * platform" — the BELL's per-item read state, which is a different
     * mechanism from the watermark above and stays that way.
     *
     * Landing on Coach Feedback clears the bell's `coach_feedback` items;
     * landing on either goals tab clears its `coach_goal` items (the
     * surface table lives in `lib/notificationsRead.js`, deliberately as
     * the inverse of the API's link table, with a test that asserts the
     * two agree). Before this, walking to the goal your coach set you left
     * the badge lit until you opened the bell — which is exactly what
     * Martin reported.
     *
     * NOT folded into the call above, even though both fire on the same
     * landing. They write different state answering different questions
     * (`schema.ts` calls merging them a defect), and this one covers goals
     * the feedback watermark knows nothing about.
     *
     * ISSUE #464 — `chosenTab`, not `tab`. `tab` carries the default, so
     * passing it meant that merely opening `/development` consumed every
     * `coach_goal` item before the player had seen the goals list, and the
     * bell never showed a number for one. An absent `?tab=` clears nothing
     * (`typesForSurface` tolerates a null tab on purpose); clicking the
     * Short-term tab writes `?tab=short` and does clear, which is the
     * "going to it on the platform" gesture #417 was asked for.
     */
    markSurfaceNotificationsRead("development", chosenTab);
  }, [tab, chosenTab]);

  // Issue #763 (D98) — the redrawn create-a-plan
  // screen replaces THIS WHOLE PAGE for a PLAYER with no goal at all, or on
  // a `?new=1` deep link — see `lib/planGate.js`'s own header for the full
  // story of each guard: the role (fix round B1 — a coach/admin never
  // reaches this screen by any path, D92; since #1166 the whole page is a
  // player's, so the gate is handed `isPlayer: true`), `goalsAnswered` (not merely
  // `goals.length === 0`, since a coach's 400 must never read as "confirmed
  // empty"), `onGoalTab` (found live: an earlier version broke journey
  // 13's `?tab=identity` deep link by swallowing non-goal tabs too), and
  // goal counting itself — RR1 (Andreas's ruling, second fix round): EVERY
  // goal counts, `completed` included, so a player with only completed
  // goals keeps today's page and can still reach their own history.
  // `createOpen` is fed from THIS page's own `useCreateDialog()` instance,
  // deliberately never the New goal sheet's — see `planCreateIntent`'s own
  // comment above. A player who already has a goal, reached with no
  // `?new=1`, still sees today's page below, unaffected — #764 (the
  // milestones screen) is what replaces it next.
  const { showNewPlan } = computePlanGate({
    isPlayer: true,
    createOpen: planCreateIntent,
    // #1164: any value that opens Coming up or Goals (an absent, unknown or
    // AI-off `?tab=` included) is a goal tab, as bare /development is.
    onGoalTab: view.section === "coming-up" || view.section === "goals",
    goalsAnswered,
    goals,
  });

  // Issue #764 (D166 M1/M2/M7) — the milestones step is a per-goal
  // drill-down, not a new route: `?milestone=<goal_id>` on THIS SAME page,
  // the same "spend a query param, don't own a route" shape #763's own
  // `?new=1` already uses. It gates BOTH the tap affordance on a goal card
  // (below, threaded into `PlanGoalsList`) and the step itself here. D166 M7
  // (player-only) is held by the page itself: it renders for a player and no
  // one else.
  const milestonesGoalId = sp.get("milestone");
  const milestoneGoal = milestonesGoalId
    ? goals.find((g) => String(g.goal_id) === milestonesGoalId) || null
    : null;
  // Reachable while `goalsAnswered` is still false (a fresh mount / a
  // reload with `?milestone=` already on the URL — the two commentary
  // states `brief-764.md` asks for, `loading` and `failed-to-load`) or
  // once a genuine 200 has resolved the goal. A resolved list that does
  // NOT contain this id (a stale link, a deleted goal) falls through to
  // today's page below rather than a fourth, undrawn error state — not
  // ruled anywhere, so the narrowest defensible reading is chosen: no
  // silent partial screen, no invented copy.
  const showMilestones = !!milestonesGoalId && (!goalsAnswered || goalsFailed || !!milestoneGoal);
  const openMilestones = (goalId) => {
    const n = new URLSearchParams(sp);
    n.set("milestone", String(goalId));
    setSp(n);
  };
  const closeMilestones = () => {
    const n = new URLSearchParams(sp);
    n.delete("milestone");
    setSp(n);
  };

  // Issue #1149 — Plan a match is one more step of this page, the way
  // `?milestone=` is: `?plan=match`, no route of its own. It wins over the
  // create-a-plan screen: a player with no goal yet can still plan a match.
  const planMatchOn = sp.get("plan") === "match";
  const closePlanMatch = () => {
    const n = new URLSearchParams(sp);
    n.delete("plan");
    setSp(n);
  };
  // Issue #1319 (D302) — a coach's game is one more step of this page:
  // `?game=<id>`, opened from its row in Coming up or from the bell. Player
  // only (this page is).
  const coachGameId = sp.get("game");
  const closeCoachGame = () => {
    const n = new URLSearchParams(sp);
    n.delete("game");
    setSp(n);
  };
  // Coming up (#1149): the chrome's own "Goals" opens the create-a-plan
  // screen through this page's `?new=1` intent; on that screen itself the
  // goal field is already on show, so Goals just takes focus there.
  const comingUp = (onAddGoal, emptyCard) => (
    <PlanComingUp goals={goals} forRole="player" onAddGoal={onAddGoal} emptyCard={emptyCard} added={addedMatch} />
  );

  if (coachGameId) {
    return <PlanCoachGame gameId={coachGameId} onBack={closeCoachGame} />;
  }

  if (planMatchOn) {
    return <PlanMatchScreen onBack={closePlanMatch} onSaved={closePlanMatch} />;
  }

  if (showMilestones) {
    return (
      <PlanMilestonesScreen
        goal={milestoneGoal}
        loading={!goalsAnswered && !goalsFailed}
        failed={goalsFailed}
        onRetry={load}
        onBack={closeMilestones}
        onGoalChanged={load}
        onGoalDeleted={async () => { await load(); closeMilestones(); }}
      />
    );
  }

  if (showNewPlan) {
    return (
      <PlanScreen
        onCreated={async () => {
          // Reload the goals list, THEN close the create intent — both
          // must land before this returns, since `save()` in PlanScreen.jsx
          // awaits this whole function before resetting its own local
          // state (its own safety-net comment explains why).
          await load();
          setPlanCreateIntent(false);
        }}
        // Issue #763 fix round, F1 (RR2 — second fix round — scopes WHEN
        // this fires) — a player who ALREADY has a goal and arrived via
        // `?new=1` (the Dashboard chooser's own `withCreate("/development")`
        // link) had no way off this screen except browser Back:
        // `createOpen` wins the gate outright, and nothing else ever
        // cleared it. Tapping the now-current "Plan" chip
        // (`kit/DestinationRow.jsx`'s own `onCurrentClick`, opt-in) clears
        // the intent instead of navigating anywhere — the gate then
        // re-settles on its own next render: false for this player
        // (they have a goal), so today's page renders.
        //
        // RR2 — passed ONLY when `planCreateIntent` is actually true (a
        // real `?new=1` to clear). For a genuine zero-goal player, this
        // screen shows through the AUTO-SWAP branch instead
        // (`onGoalTab && goalsAnswered && goals.length === 0`, no
        // `createOpen` involved) — tapping "Plan" there would clear a
        // create intent that was never set, land back on the exact same
        // gate result, and do nothing. `undefined` in that case, so
        // `DestinationRow`'s current chip stays the plain, non-interactive
        // `<span>` it always was — no `role="button"`, no cursor, no
        // keyboard handler for a tap that has nothing to do.
        onCurrentDestinationTap={planCreateIntent ? () => setPlanCreateIntent(false) : undefined}
        comingUp={comingUp(() => document.querySelector('[data-testid="goal-target"]')?.focus(), false)}
      />
    );
  }

  // Issue #940 (D175) — `DevelopmentChrome` owns the navy
  // TopBar, D92's DestinationRow and the header; no shipped header renders
  // here.
  //
  // Issue #1164 (D219, D233.5, D243) — three sections under the ribbon,
  // Coming up, Goals and More, replace the five chrome tabs. The URL keeps its
  // one `?tab=` parameter (`lib/planSections.js` maps every old value to a
  // section, a fold or a goal group). Strip taps and fold toggles write it with
  // `replace`. Which section opens is the URL's alone (D254: no memory), so a
  // bare `/development` is always Coming up. "New goal" (D175 P3) lives in
  // Goals only, as a navy `SecondaryAction`.
  const setTab = (value) => {
    const n = new URLSearchParams(sp);
    n.set("tab", value);
    setSp(n, { replace: true });
  };
  const SECTION_ITEMS = [
    { key: "coming-up", label: "Coming up", testId: "plan-section-coming-up" },
    { key: "goals", label: "Goals", testId: "plan-section-goals" },
    { key: "more", label: "More", testId: "plan-section-more" },
  ];
  const SECTION_TITLES = { "coming-up": "Coming up", goals: "Goals", more: "More" };
  const folds = [
    { key: "reflections", title: "Reflections", sub: "Your log of matches, training and notes", body: <Reflections /> },
    {
      key: "feedback",
      title: feedbackFailed || hasCoach === null ? "Coach feedback" : `Coach feedback \u00b7 ${notes.length}`,
      sub: "What your coach has written to you",
      body: <PlayerCoachFeedback notes={notes} hasCoach={hasCoach} failed={feedbackFailed} onRetry={loadFeedback} />,
    },
    { key: "identity", title: "Identity & vision", sub: "Who you are as a player, and where you're going", body: <PlayerIdentity appearance="r5" /> },
  ];
  if (AI_COACH_ENABLED) {
    folds.push({ key: "ai", title: "AI Development Plan", body: <AiPlanPanel onSaved={load} /> });
  }

  let body;
  if (view.section === "goals") {
    // #1219 (D282, D283): the board's cards and the kit New goal sheet.
    body = (
      <div className="flex flex-col" style={{ gap: 14 }} data-testid="plan-goals">
        <NewGoalSheet onCreated={load} />
        {goalsAnswered || goalsFailed ? (
          <PlanGoalsList goals={goals} failed={goalsFailed} onRetry={load} onOpenMilestones={openMilestones} groupRefs={groupRefs} />
        ) : null}
      </div>
    );
  } else if (view.section === "more") {
    body = <PlanMoreFolds folds={folds} openKey={view.fold} onToggle={(key) => setTab(key === view.fold ? "more" : key)} />;
  } else {
    body = comingUp(() => setPlanCreateIntent(true), true);
  }

  return (
    <div data-testid="development-page">
      <DevelopmentChrome
        sections={<SectionStrip data-testid="plan-sections" idPrefix="plan-sections" items={SECTION_ITEMS} currentKey={section} onChange={setTab} />}
        title={SECTION_TITLES[view.section]}
      >
        <SectionPanel idPrefix="plan-sections" currentKey={view.section} className="flex flex-col" style={{ gap: 14 }}>
          {body}
        </SectionPanel>
      </DevelopmentChrome>
    </div>
  );
}

// ---------------- AI Plan panel ----------------
function AiPlanPanel({ onSaved }) {
  const [horizon, setHorizon] = useState("short");
  const [ambition, setAmbition] = useState("");
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(false);
  const [cached, setCached] = useState(false);
  const [saving, setSaving] = useState(false);

  const run = async (force = false) => {
    setBusy(true);
    try {
      const { data } = await api.post("/development-plan/generate", {
        horizon,
        long_term_ambition: ambition || null,
        force_refresh: force,
      });
      setPlan(data.data); setCached(data.cached);
    } catch { toast.error("AI generation failed"); }
    setBusy(false);
  };

  const acceptAll = async () => {
    if (!plan) return;
    const items = [];
    for (const cat of ["technical", "tactical", "physical", "mental"]) {
      for (const o of plan[cat] || []) {
        items.push({
          category: cat, horizon,
          target: o.target, actions: o.actions || [],
          due_date: o.due_date, notes: o.notes || "",
        });
      }
    }
    if (!items.length) return toast.error("Nothing to save");
    setSaving(true);
    try {
      const { data } = await api.post("/development-plan/accept", items);
      toast.success(`Added ${data.created} objectives to your plan`);
      onSaved();
    } catch { toast.error("Failed to save"); }
    setSaving(false);
  };

  const acceptOne = async (cat, o) => {
    try {
      await api.post("/development-plan/accept", [{
        category: cat, horizon,
        target: o.target, actions: o.actions || [],
        due_date: o.due_date, notes: o.notes || "",
      }]);
      toast.success("Added to your plan");
      onSaved();
    } catch { toast.error("Failed"); }
  };

  return (
    <div className="space-y-6">
      <Card className="border-slate-200">
        <CardContent className="p-5 space-y-3">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: "#D4A01720" }}>
              <Sparkles size={18} color="#D4A017" />
            </div>
            <div>
              <h2 className="font-display font-bold text-lg text-slate-900">AI-generated development plan</h2>
              <p className="text-sm text-slate-500">Analyses your matches, statistics, journal entries and position to craft a personalised plan across four dimensions.</p>
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-3 items-end">
            <div>
              <Label>Horizon</Label>
              <Select value={horizon} onValueChange={setHorizon}>
                <SelectTrigger data-testid="ai-horizon"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="short">Short-term (4–12 weeks)</SelectItem>
                  <SelectItem value="long">Long-term (6–18 months)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Label>Long-term ambition (optional)</Label>
              <Input value={ambition} onChange={(e) => setAmbition(e.target.value)} placeholder="e.g. Make First XI · Represent province · Become opening batter" data-testid="ai-ambition" />
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => run(false)} disabled={busy} style={{ background: "#0B2E59" }} data-testid="ai-generate">
              {busy ? <><Loader2 size={16} className="mr-2 animate-spin" /> Generating…</> : <><Sparkles size={16} className="mr-2" /> Generate plan</>}
            </Button>
            {plan && <Button variant="outline" onClick={() => run(true)} disabled={busy} data-testid="ai-refresh"><RotateCw size={14} className="mr-2" /> Refresh</Button>}
            {plan && <Button onClick={acceptAll} disabled={saving} style={{ background: "#D4A017" }} data-testid="ai-accept-all">
              {saving ? <><Loader2 size={16} className="mr-2 animate-spin" /> Adding…</> : <><Check size={16} className="mr-2" /> Add all to my plan</>}
            </Button>}
          </div>
        </CardContent>
      </Card>

      {plan && (
        <div className="space-y-6" data-testid="ai-plan-output">
          {cached && <Badge variant="outline" className="text-[10px]">cached</Badge>}
          {plan.summary && (
            <Card className="border-slate-200 overflow-hidden">
              <div className="p-6 border-l-4" style={{ borderColor: "#D4A017", background: "linear-gradient(90deg, #FEF9E7 0%, transparent 100%)" }}>
                <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-2 flex items-center gap-1"><Sparkles size={12} color="#D4A017" /> Positioning</div>
                <p className="text-slate-800">{plan.summary}</p>
              </div>
            </Card>
          )}

          <div className="grid gap-6">
            {["technical", "tactical", "physical", "mental"].map((cat) => {
              const list = plan[cat] || [];
              if (!list.length) return null;
              const c = CATEGORIES[cat];
              const CatIcon = c.Icon;
              return (
                <Card key={cat} className="border-slate-200" data-testid={`ai-cat-${cat}`}>
                  <CardContent className="p-6">
                    <div className="flex items-center gap-2 mb-4">
                      <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: `${c.color}20` }}>
                        <CatIcon size={18} color={c.color} />
                      </div>
                      <h3 className="font-display font-bold text-lg text-slate-900">{c.label} objectives</h3>
                      <Badge variant="outline" className="ml-auto">{list.length}</Badge>
                    </div>
                    <div className="grid md:grid-cols-2 gap-3">
                      {list.map((o, i) => (
                        <div key={i} className="min-w-0 p-4 rounded-xl border border-slate-200 flex flex-col gap-2">
                          <div className="flex items-start justify-between gap-2">
                            {/* #251: model-written text of unbounded length
                                beside a fixed-width Add button. */}
                            <div className="min-w-0 font-semibold text-slate-900 break-words">{o.target}</div>
                            <Button size="sm" variant="outline" onClick={() => acceptOne(cat, o)} data-testid={`ai-accept-${cat}-${i}`}>
                              <Plus size={12} className="mr-1" /> Add
                            </Button>
                          </div>
                          {o.notes && <p className="text-xs text-slate-500">{o.notes}</p>}
                          {o.actions?.length > 0 && (
                            <div className="mt-1">
                              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Actions</div>
                              <ul className="space-y-1">
                                {o.actions.map((a, j) => (
                                  <li key={j} className="text-sm text-slate-700 flex gap-2">
                                    <span className="w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0" style={{ background: c.color }} />{a}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {o.due_date && <div className="text-xs text-slate-500 mt-1">Due {o.due_date}</div>}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {!plan && !busy && (
        <div className="text-center py-16">
          <div className="w-14 h-14 rounded-2xl mx-auto flex items-center justify-center mb-3" style={{ background: "#D4A01720" }}>
            <Sparkles size={22} color="#D4A017" />
          </div>
          <p className="text-slate-500">Generate a personalised, evidence-backed development plan.</p>
        </div>
      )}
    </div>
  );
}
